import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { REQUEST_TYPES, SERVICE_TYPES, URGENCIES } from "../../lib/gmaoConstants";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { AddressAutocomplete } from "../ui/address-autocomplete";

const emptyForm = {
  requestType: "urgence",
  serviceType: "vitrerie",
  urgency: "normale",
  address: "",
  postalCode: "",
  city: "",
  floor: "",
  digicode: "",
  accessInfo: "",
  tenantName: "",
  tenantPhone: "",
  preferredSlot: "",
  description: "",
  clientId: "",
};

const InterventionForm = ({
  open,
  onOpenChange,
  onCreated,
  showTenant = true,
  clients = null, // si fourni (admin) → sélecteur de client
}) => {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleAddress = (addr) => {
    setForm((f) => ({
      ...f,
      address: addr.address || addr.fullAddress || "",
      postalCode: addr.postalCode || "",
      city: addr.city || "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.address || form.address.length < 3) {
      toast.error("Veuillez renseigner l'adresse de l'intervention.");
      return;
    }
    if (!form.description || form.description.length < 5) {
      toast.error("Veuillez décrire le problème (5 caractères minimum).");
      return;
    }
    setLoading(true);
    const payload = {
      requestType: form.requestType,
      serviceType: form.serviceType,
      urgency: form.urgency,
      description: form.description,
      address: form.address,
      postalCode: form.postalCode || null,
      city: form.city || null,
      floor: form.floor || null,
      digicode: form.digicode || null,
      accessInfo: form.accessInfo || null,
      tenantName: showTenant ? form.tenantName || null : null,
      tenantPhone: showTenant ? form.tenantPhone || null : null,
      preferredSlot: form.preferredSlot || null,
    };
    if (clients && form.clientId) {
      payload.clientId = form.clientId;
    }
    try {
      const { data } = await API.post("/interventions", payload);
      toast.success(`Demande créée : ${data.dossierNumber}`);
      setForm(emptyForm);
      onOpenChange(false);
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="intervention-form-dialog">
        <DialogHeader>
          <DialogTitle>Nouvelle demande d'intervention</DialogTitle>
          <DialogDescription>
            Décrivez votre besoin. Un numéro de dossier sera généré automatiquement.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {clients && (
            <div className="space-y-2">
              <Label>Client / Agence (optionnel)</Label>
              <Select value={form.clientId} onValueChange={(v) => set("clientId", v)}>
                <SelectTrigger data-testid="form-client-select">
                  <SelectValue placeholder="Au nom de NGL (interne)" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} {c.company ? `— ${c.company}` : ""} ({c.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Type de demande</Label>
              <Select value={form.requestType} onValueChange={(v) => set("requestType", v)}>
                <SelectTrigger data-testid="form-request-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REQUEST_TYPES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Service</Label>
              <Select value={form.serviceType} onValueChange={(v) => set("serviceType", v)}>
                <SelectTrigger data-testid="form-service-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Degré d'urgence</Label>
              <Select value={form.urgency} onValueChange={(v) => set("urgency", v)}>
                <SelectTrigger data-testid="form-urgency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {URGENCIES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Adresse du bien</Label>
            <AddressAutocomplete
              onAddressSelect={handleAddress}
              initialValue={form.address}
              placeholder="Commencez à taper l'adresse..."
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Code postal</Label>
              <Input value={form.postalCode} onChange={(e) => set("postalCode", e.target.value)} data-testid="form-postal" />
            </div>
            <div className="space-y-2">
              <Label>Ville</Label>
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="form-city" />
            </div>
            <div className="space-y-2">
              <Label>Étage</Label>
              <Input value={form.floor} onChange={(e) => set("floor", e.target.value)} data-testid="form-floor" />
            </div>
            <div className="space-y-2">
              <Label>Digicode</Label>
              <Input value={form.digicode} onChange={(e) => set("digicode", e.target.value)} data-testid="form-digicode" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Informations d'accès</Label>
            <Input value={form.accessInfo} onChange={(e) => set("accessInfo", e.target.value)} placeholder="Bâtiment, interphone, étage..." data-testid="form-access" />
          </div>

          {showTenant && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom du locataire (si applicable)</Label>
                <Input value={form.tenantName} onChange={(e) => set("tenantName", e.target.value)} data-testid="form-tenant-name" />
              </div>
              <div className="space-y-2">
                <Label>Téléphone du locataire</Label>
                <Input value={form.tenantPhone} onChange={(e) => set("tenantPhone", e.target.value)} data-testid="form-tenant-phone" />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Créneau souhaité</Label>
            <Input value={form.preferredSlot} onChange={(e) => set("preferredSlot", e.target.value)} placeholder="Ex : demain matin, en semaine après 17h..." data-testid="form-slot" />
          </div>

          <div className="space-y-2">
            <Label>Description du problème</Label>
            <Textarea
              rows={4}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Décrivez précisément le problème..."
              data-testid="form-description"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="form-cancel">
              Annuler
            </Button>
            <Button type="submit" className="btn-primary" disabled={loading} data-testid="form-submit">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Créer la demande
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InterventionForm;
